/**
 * alertRulesApi.ts
 * Feature: 125 - Alert Rule Management
 * Author: Peter Dobler
 * Date: 2026-03-25
 * Constitution Version: 1.12.0
 *
 * Purpose: API client functions for alert rule operations.
 */

import { apiClient } from './apiClient';
import type {
  AlertRuleView,
  AlertRuleListResponse,
  AlertRuleFilters,
  PaginationParams,
  SortParams,
  CreateAlertRuleRequest,
  UpdateAlertRuleRequest,
  ToggleAlertRuleRequest,
  ValidateNameRequest,
  ValidateNameResponse,
  AlertTypeOption,
  SeverityOption,
  RuleAssignments,
  AddAssignmentRequest,
} from '@shared/types/alertRuleManagement';

// =============================================================================
// API Base Path
// =============================================================================

const BASE_PATH = '/api/alert-rules';

// =============================================================================
// List Alert Rules
// =============================================================================

export interface FetchAlertRulesParams {
  filters?: AlertRuleFilters;
  pagination?: PaginationParams;
  sort?: SortParams;
}

/**
 * Fetch paginated list of alert rules with filtering
 */
export async function fetchAlertRules(
  params: FetchAlertRulesParams = {}
): Promise<AlertRuleListResponse> {
  const { filters = {}, pagination = { page: 1, pageSize: 25 }, sort = { sortBy: 'name', sortOrder: 'asc' } } = params;

  const queryParams = new URLSearchParams();

  // Pagination
  queryParams.set('page', String(pagination.page));
  queryParams.set('pageSize', String(pagination.pageSize));

  // Sort
  queryParams.set('sortBy', sort.sortBy);
  queryParams.set('sortOrder', sort.sortOrder);

  // Filters
  if (filters.alertType) {
    queryParams.set('alertType', filters.alertType);
  }
  if (filters.severity) {
    queryParams.set('severity', filters.severity);
  }
  if (filters.enabled !== undefined) {
    queryParams.set('enabled', String(filters.enabled));
  }
  if (filters.search) {
    queryParams.set('search', filters.search);
  }

  const response = await apiClient.get<AlertRuleListResponse>(
    `${BASE_PATH}?${queryParams.toString()}`
  );

  return response.data;
}

// =============================================================================
// Get Single Alert Rule
// =============================================================================

/**
 * Fetch a single alert rule by ID
 */
export async function fetchAlertRuleById(id: string): Promise<AlertRuleView | null> {
  try {
    const response = await apiClient.get<AlertRuleView>(`${BASE_PATH}/${id}`);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

// =============================================================================
// Create Alert Rule
// =============================================================================

/**
 * Create a new alert rule
 */
export async function createAlertRule(data: CreateAlertRuleRequest): Promise<AlertRuleView> {
  const response = await apiClient.post<AlertRuleView>(BASE_PATH, data);
  return response.data;
}

// =============================================================================
// Update Alert Rule
// =============================================================================

/**
 * Update an existing alert rule
 */
export async function updateAlertRule(
  id: string,
  data: UpdateAlertRuleRequest
): Promise<AlertRuleView> {
  const response = await apiClient.put<AlertRuleView>(`${BASE_PATH}/${id}`, data);
  return response.data;
}

// =============================================================================
// Toggle Alert Rule
// =============================================================================

/**
 * Toggle alert rule enabled status
 */
export async function toggleAlertRule(
  id: string,
  data: ToggleAlertRuleRequest
): Promise<AlertRuleView> {
  const response = await apiClient.patch<AlertRuleView>(`${BASE_PATH}/${id}/toggle`, data);
  return response.data;
}

// =============================================================================
// Delete Alert Rule
// =============================================================================

/**
 * Delete an alert rule
 */
export async function deleteAlertRule(id: string): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/${id}`);
}

// =============================================================================
// Validate Name
// =============================================================================

/**
 * Validate if an alert rule name is unique
 */
export async function validateAlertRuleName(
  data: ValidateNameRequest
): Promise<ValidateNameResponse> {
  const response = await apiClient.post<ValidateNameResponse>(`${BASE_PATH}/validate-name`, data);
  return response.data;
}

// =============================================================================
// Reference Data
// =============================================================================

/**
 * Fetch available alert types
 */
export async function fetchAlertTypes(): Promise<AlertTypeOption[]> {
  const response = await apiClient.get<AlertTypeOption[]>(`${BASE_PATH}/types`);
  return response.data;
}

/**
 * Fetch available severity levels
 */
export async function fetchSeverities(): Promise<SeverityOption[]> {
  const response = await apiClient.get<SeverityOption[]>(`${BASE_PATH}/severities`);
  return response.data;
}

// =============================================================================
// Spec 192 — Assignment management (FR-009)
// =============================================================================

/**
 * Fetch a rule's capacity/lakehouse assignments with display names + scope.
 */
export async function fetchRuleAssignments(id: string): Promise<RuleAssignments> {
  const response = await apiClient.get<RuleAssignments>(`${BASE_PATH}/${id}/assignments`);
  return response.data;
}

/**
 * Add an assignment (exactly one of capacityId | lakehouseId). Idempotent.
 */
export async function addRuleAssignment(
  id: string,
  target: AddAssignmentRequest
): Promise<{ outcome: 'created' | 'exists' }> {
  const response = await apiClient.post<{ outcome: 'created' | 'exists' }>(
    `${BASE_PATH}/${id}/assignments`,
    target
  );
  return response.data;
}

/**
 * Remove an assignment. Returns the rule's post-removal scope hint.
 */
export async function removeRuleAssignment(
  id: string,
  target: { capacityId?: string; lakehouseId?: string }
): Promise<{ scope: 'global' | 'scoped' | 'unassigned' }> {
  const path = target.capacityId !== undefined
    ? `${BASE_PATH}/${id}/assignments/capacity/${target.capacityId}`
    : `${BASE_PATH}/${id}/assignments/lakehouse/${target.lakehouseId}`;
  const response = await apiClient.delete<{ scope: 'global' | 'scoped' | 'unassigned' }>(path);
  return response.data;
}
